import { FC, memo, useCallback, useMemo } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Checkbox from "@mui/material/Checkbox";
import { useScoringStore, MAX_ACTIVE_PROFILES } from "@/store/scoringStore";
import type { ScoringModel } from "@/apis/argo/scoringModels";
import { useCompanyFiltersStore } from "../../provider/CompanyFilterProvider";
import { hasCriteria, type ModelScoreSlice } from "./slice";
import {
  ModelScoreEntryEditor,
  type UpdateEntryFn,
} from "./ModelScoreEntryEditor";

const AT_CAP_MESSAGE = `Scoring is limited to ${MAX_ACTIVE_PROFILES} active profiles/models — deselect one to add another.`;

/**
 * V2 Model Score filter: pick one or more scoring models inline (no need to
 * activate them from the toolbar first) and set per-model tier / score-range
 * criteria. Confirming the filter activates the selected models on the list
 * (RGI-2348).
 */
const ModelScoreFilter: FC = memo(() => {
  const slice = useCompanyFiltersStore(
    (s) => s.slices.modelScore
  ) as ModelScoreSlice;
  const setSlice = useCompanyFiltersStore((s) => s.setSlice);

  const models = useScoringStore((s) => s.models);
  const isModelsLoading = useScoringStore((s) => s.isModelsLoading);
  const modelsError = useScoringStore((s) => s.modelsError);
  const activeSourceIds = useScoringStore((s) => s.activeSourceIds);
  const activeModelFamilies = useScoringStore((s) => s.activeModelFamilies);

  const selectedIds = useMemo(
    () => new Set(slice.entries.map((e) => e.modelId)),
    [slice.entries]
  );

  // Confirming makes the active V2 models match this selection, and active
  // scoring sources share a budget of MAX_ACTIVE_PROFILES — so the future
  // total is the non-model sources (V1 profiles) plus the selection. Block
  // further selections once that total hits the cap.
  const outOfBudget = useMemo(() => {
    const nonModelActiveCount =
      activeSourceIds.length - activeModelFamilies.length;
    return nonModelActiveCount + slice.entries.length >= MAX_ACTIVE_PROFILES;
  }, [activeSourceIds, activeModelFamilies, slice.entries]);

  const sortedModels = useMemo(
    () => [...models].sort((a, b) => a.name.localeCompare(b.name)),
    [models]
  );
  const selectedModels = useMemo(
    () =>
      slice.entries
        .map((e) => models.find((m) => m.family === e.modelId))
        .filter((m): m is ScoringModel => m !== undefined),
    [slice.entries, models]
  );

  const handleSelectionChange = useCallback(
    (next: ScoringModel[]) => {
      setSlice("modelScore", (prev: ModelScoreSlice) => {
        const byId = new Map(prev.entries.map((e) => [e.modelId, e]));
        // Entries whose model is missing from the catalog can't appear in the
        // Autocomplete value — preserve them so a deleted/unpublished model
        // keeps its saved criteria.
        const unknown = prev.entries.filter(
          (e) => !models.some((m) => m.family === e.modelId)
        );
        return {
          entries: [
            ...next.map((m) => byId.get(m.family) ?? { modelId: m.family }),
            ...unknown,
          ],
        };
      });
    },
    [setSlice, models]
  );

  const updateEntry = useCallback<UpdateEntryFn>(
    (modelId, updater) => {
      setSlice("modelScore", (prev: ModelScoreSlice) => ({
        entries: prev.entries.map((e) =>
          e.modelId === modelId ? updater(e) : e
        ),
      }));
    },
    [setSlice]
  );

  // Clears the filter criteria only — the selection mirrors the applied
  // scoring models, so deselecting is done per model via the dropdown.
  const handleClear = useCallback(() => {
    setSlice("modelScore", (prev: ModelScoreSlice) => ({
      entries: prev.entries.map((e) => ({ modelId: e.modelId })),
    }));
  }, [setSlice]);

  if (models.length === 0) {
    if (isModelsLoading) {
      return (
        <Box
          data-testid="scoring-model-filter-loading"
          sx={{ display: "flex", justifyContent: "center", py: 3 }}
        >
          <CircularProgress size={24} />
        </Box>
      );
    }
    return (
      <Alert
        severity={modelsError ? "error" : "info"}
        data-testid="scoring-model-filter-empty-state"
        sx={{ fontSize: "0.8125rem" }}
      >
        {modelsError ??
          "No AI scoring models are available for your workspace yet."}
      </Alert>
    );
  }

  return (
    <Box
      data-testid="scoring-model-score-filter"
      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Typography variant="subtitle2">Scoring models</Typography>
        <Typography variant="body2" color="text.secondary">
          Selected models are applied to the list. Add a tier or score range to
          filter companies by them.
        </Typography>
        <Autocomplete
          multiple
          size="small"
          options={sortedModels}
          value={selectedModels}
          onChange={(_, next) => handleSelectionChange(next)}
          isOptionEqualToValue={(option, val) => option.family === val.family}
          getOptionLabel={(option) => option.name}
          getOptionDisabled={(option) =>
            outOfBudget && !selectedIds.has(option.family)
          }
          disableCloseOnSelect
          limitTags={2}
          data-testid="scoring-model-filter-picker"
          renderOption={(props, option, { selected }) => (
            <li
              {...props}
              key={option.family}
              data-testid={`scoring-model-filter-option-${option.family}`}
            >
              <Checkbox size="small" checked={selected} sx={{ mr: 1 }} />
              {option.name}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={
                selectedModels.length > 0 ? undefined : "Search scoring models"
              }
              helperText={outOfBudget ? AT_CAP_MESSAGE : undefined}
              inputProps={{
                ...params.inputProps,
                "data-testid": "scoring-model-filter-picker-input",
              }}
            />
          )}
        />
      </Box>

      {slice.entries.map((entry) => (
        <Box
          key={entry.modelId}
          sx={{ display: "flex", flexDirection: "column", gap: 2 }}
        >
          <Divider />
          <ModelScoreEntryEditor
            entry={entry}
            model={models.find((m) => m.family === entry.modelId)}
            onUpdate={updateEntry}
          />
        </Box>
      ))}

      {slice.entries.some(hasCriteria) && (
        <Button
          size="small"
          onClick={handleClear}
          data-testid="scoring-model-score-clear-btn"
          sx={{ alignSelf: "flex-start" }}
        >
          Clear
        </Button>
      )}
    </Box>
  );
});

ModelScoreFilter.displayName = "ModelScoreFilter";

export { ModelScoreFilter };
